import { Building2, FileCheck, MessageSquareQuote, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { CertificateRequestIntentDto } from "@/services/certificateRequestIntentsService";
import { cn } from "@/lib/utils";

const INTENT_ICONS: Record<string, typeof FileCheck> = {
  certificates: FileCheck,
  open_company: Building2,
  garantia_alquiler: Shield,
  quote_request: MessageSquareQuote,
};

function intentIcon(slug: string): typeof FileCheck {
  return INTENT_ICONS[slug] ?? FileCheck;
}

export function NewRequestIntentSelection({
  intents,
  isLoading,
  value,
  onValueChange,
}: {
  intents: CertificateRequestIntentDto[];
  isLoading: boolean;
  value: string | null;
  onValueChange: (value: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const active = intents.filter((i) => i.status === "active");
  const comingSoon = intents.filter((i) => i.status === "coming_soon");

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center py-12">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (intents.length === 0) {
    return (
      <EmptyState
        title={t("requests.user.wizard.intentsEmptyTitle")}
        message={t("requests.user.wizard.intentsEmptyMessage")}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Label className="text-base font-semibold text-foreground">
        {t("requests.user.wizard.intentSectionLabel")}
      </Label>
      <div className="grid grid-cols-1 gap-3">
        {active.length > 0 ? (
          <RadioGroup
            value={value ?? ""}
            onValueChange={onValueChange}
            className="contents"
          >
            {active.map((intent) => {
              const Icon = intentIcon(intent.slug);
              const selected = value === intent.slug;
              const titleKey = `requests.user.wizard.intents.${intent.slug}.title`;
              const descKey = `requests.user.wizard.intents.${intent.slug}.description`;
              const title = t(titleKey, { defaultValue: intent.slug });
              const description = t(descKey, { defaultValue: "" });
              return (
                <div key={intent.slug}>
                  <RadioGroupItem
                    value={intent.slug}
                    id={`nr-intent-${intent.slug}`}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={`nr-intent-${intent.slug}`}
                    className={cn(
                      "wizard-selectable-card flex cursor-pointer flex-col gap-3 border-2 p-4 transition-colors duration-200",
                      "peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2",
                      selected
                        ? "border-primary bg-primary/[0.06] dark:border-primary dark:bg-primary/10"
                        : "border-border hover:border-primary dark:border-border/80 dark:bg-secondary/35 dark:shadow-md dark:shadow-black/20 dark:hover:border-primary",
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={cn(
                          "wizard-selectable-card-icon border-2 transition-colors duration-200",
                          selected
                            ? "border-primary text-primary dark:border-primary"
                            : "border-border bg-background dark:border-border/80 dark:bg-secondary/55",
                        )}
                        aria-hidden
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.75} />
                      </span>
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <span className="block text-base font-semibold leading-snug text-foreground">
                          {title}
                        </span>
                        {description !== "" ? (
                          <span
                            className={cn(
                              "block text-sm leading-relaxed",
                              selected ? "text-muted-foreground" : "text-foreground/80",
                            )}
                          >
                            {description}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
        ) : null}

        {comingSoon.map((intent) => {
          const Icon = intentIcon(intent.slug);
          const titleKey = `requests.user.wizard.intents.${intent.slug}.title`;
          const descKey = `requests.user.wizard.intents.${intent.slug}.description`;
          const title = t(titleKey, { defaultValue: intent.slug });
          const description = t(descKey, { defaultValue: "" });
          return (
            <div
              key={intent.slug}
              className={cn(
                "pointer-events-none flex flex-col gap-3 rounded-xl border border-dashed border-border/70 bg-muted/30 p-4 shadow-none",
                "dark:border-border/40 dark:bg-muted/15 dark:shadow-none",
              )}
              aria-disabled
            >
              <div className="flex items-start gap-3">
                <span
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40 text-muted-foreground dark:border-border/40 dark:bg-muted/25"
                  aria-hidden
                >
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-semibold leading-snug text-muted-foreground">
                      {title}
                    </span>
                    <Badge
                      variant="secondary"
                      className="shrink-0 border-transparent bg-muted font-normal text-muted-foreground dark:bg-muted/50"
                    >
                      {t("requests.user.wizard.comingSoon")}
                    </Badge>
                  </div>
                  {description !== "" ? (
                    <span className="block text-sm leading-relaxed text-muted-foreground/80">
                      {description}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
